import React, { useEffect, useState, useCallback } from 'react';
import { makeStyles, Theme, Typography, Box, CircularProgress } from '@material-ui/core';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    borderRadius: '16px',
    padding: '24px',
    minWidth: '160px',
  },
  timer: {
    color: 'white',
    fontSize: '48px',
    fontWeight: 'bold',
    marginBottom: '16px',
  },
  label: {
    color: 'white',
    fontSize: '16px',
    marginBottom: '24px',
  },
  progress: {
    color: 'white',
  },
}));

interface CountdownTimerProps {
  duration: number; // 秒単位
  onTimerEnd: () => void;
  isVisible: boolean;
}

export default function CountdownTimer({ duration, onTimerEnd, isVisible }: CountdownTimerProps) {
  const classes = useStyles();
  const [timeLeft, setTimeLeft] = useState(duration);
  const [progress, setProgress] = useState(100);
  const { room } = useVideoContext();

  const formatTime = useCallback((seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);

  useEffect(() => {
    if (!isVisible) {
      setTimeLeft(duration);
      setProgress(100);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        const newTime = prevTime - 1;
        const newProgress = (newTime / duration) * 100;
        setProgress(newProgress);

        if (newTime <= 0) {
          clearInterval(timer);
          onTimerEnd();
          return 0;
        }

        return newTime;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [duration, isVisible, onTimerEnd]);

  // 非表示の場合、または残り2秒以下の場合は表示しない（終了直前のずれを隠す）
  if (!isVisible || timeLeft <= 2) {
    return null;
  }

  return (
    <Box className={classes.container}>
      <Typography className={classes.label}>通話時間</Typography>
      <Typography className={classes.timer}>{formatTime(timeLeft)}</Typography>
      <CircularProgress variant="determinate" value={progress} size={60} thickness={4} className={classes.progress} />
    </Box>
  );
}
